// src/pages/Admin/Communications/EmailCampaigns.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Mail, 
  Users, 
  TrendingUp, 
  Send, 
  Plus, 
  Search,
  CheckCircle,
  UserCheck,
  MoreVertical
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import unifiedAuth from '../../../utils/unifiedAuth';
import { config } from '../../../config';

interface NewsletterSubscriber {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  isVerified: boolean;
  source: string;
  createdAt: string;
  verifiedAt?: string;
}

interface EmailStats {
  total_subscriptions: number;
  verified_subscriptions: number;
  unverified_subscriptions: number;
}

interface EmailCampaign {
  id: string;
  subject: string;
  content: string;
  status: 'draft' | 'sent' | 'scheduled';
  recipientCount: number;
  sentAt?: string;
  scheduledAt?: string;
  openRate?: number;
  clickRate?: number;
}

const EmailCampaigns: React.FC = () => {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [_campaigns, _setCampaigns] = useState<EmailCampaign[]>([]);
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'verified' | 'unverified'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Campaign creation
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    subject: '',
    content: '',
    fromEmail: 'hello',
    targetAudience: 'all',
    scheduleDate: '',
    scheduleTime: ''
  });
  const [sending, setSending] = useState(false);
  
  const { showSuccess, showError } = useToast();

  // Email alias options
  const emailAliasOptions = [
    { value: 'hello', label: 'hello@strymhub.com - Welcome & Newsletter', description: 'Best for promotional content' },
    { value: 'admin', label: 'admin@strymhub.com - Admin Updates', description: 'For administrative announcements' },
    { value: 'contact', label: 'contact@strymhub.com - Contact Form', description: 'For general communications' },
    { value: 'support', label: 'support@strymhub.com - Support', description: 'For support-related updates' },
    { value: 'info', label: 'info@strymhub.com - Information', description: 'For general information emails' }
  ];

  const audienceOptions = [
    { value: 'all', label: 'All Users & Subscribers', description: 'All registered users + newsletter subscribers' },
    { value: 'active', label: 'Active Users', description: 'Registered users who logged in within 90 days' },
    { value: 'admins', label: 'Admin Users', description: 'Admin and staff users only' },
    { value: 'subscribers', label: 'Newsletter Subscribers Only', description: 'Only newsletter subscribers (not registered users)' }
  ];

  // Handle modal backdrop click
  const handleCampaignModalBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setShowCampaignModal(false);
    }
  };

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (showCampaignModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCampaignModal]);

  // Load email statistics
  const loadEmailStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/newsletter/stats/`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmailStats(data);
      } else {
        console.error('Failed to fetch email stats:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to load email stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Load newsletter subscribers
  const loadSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (currentPage - 1).toString(),
        size: '20',
        sortBy: 'createdAt',
        sortDir: 'desc'
      });

      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/newsletter/subscriptions/?${params}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.content || []);
        setTotalPages(data.totalPages || 1);
      } else {
        console.error('Failed to fetch subscribers:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to load subscribers:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  // Manually verify a subscriber
  const verifySubscriber = async (subscriberId: string) => {
    try {
      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/newsletter/verify-admin/${subscriberId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.message || 'Subscriber verified successfully!');
        loadSubscribers(); // Refresh subscriber list
        loadEmailStats(); // Refresh stats
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to verify subscriber');
      }
    } catch (error) {
      console.error('Failed to verify subscriber:', error);
      showError('Failed to verify subscriber. Please try again.');
    }
  };

  // Send newsletter campaign
  const sendCampaign = async () => {
    if (!campaignForm.subject.trim() || !campaignForm.content.trim()) {
      showError('Please fill in both subject and content');
      return;
    }

    try {
      setSending(true);
      const params = new URLSearchParams({
        subject: campaignForm.subject,
        content: campaignForm.content,
        fromEmail: campaignForm.fromEmail,
        targetAudience: campaignForm.targetAudience
      });

      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/newsletter/send/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      const data = await response.json();

      if (response.ok) {
        const recipientCount = data.recipient_count || 0;
        const successCount = data.success_count || 0;
        const failedCount = data.failed_count || 0;

        if (recipientCount === 0) {
          showError('No recipients found for the selected audience.');
        } else if (failedCount > 0 && successCount > 0) {
          showSuccess(`Sent to ${successCount} of ${recipientCount} recipients (${failedCount} failed) from ${campaignForm.fromEmail}@strymhub.com`);
          setCampaignForm({ subject: '', content: '', fromEmail: 'hello', targetAudience: 'all', scheduleDate: '', scheduleTime: '' });
          setShowCampaignModal(false);
        } else {
          showSuccess(`Newsletter sent successfully to ${successCount} recipients from ${campaignForm.fromEmail}@strymhub.com!`);
          setCampaignForm({ subject: '', content: '', fromEmail: 'hello', targetAudience: 'all', scheduleDate: '', scheduleTime: '' });
          setShowCampaignModal(false);
        }
        loadEmailStats(); // Refresh stats
      } else {
        showError(data.message || 'Failed to send newsletter');
      }
    } catch (error) {
      console.error('Failed to send campaign:', error);
      showError('Failed to send newsletter. Please try again.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    loadEmailStats();
    loadSubscribers();
  }, [loadEmailStats, loadSubscribers]);

  // Filter subscribers based on search and status
  const filteredSubscribers = subscribers.filter(subscriber => {
    const matchesSearch = !searchTerm || 
      subscriber.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.lastName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && subscriber.isActive) ||
      (statusFilter === 'verified' && subscriber.isVerified) ||
      (statusFilter === 'unverified' && !subscriber.isVerified);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Email Campaigns</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage newsletter subscribers and email campaigns</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Subscribers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : emailStats?.total_subscriptions?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Verified Subscribers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : emailStats?.verified_subscriptions?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Verification Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : emailStats?.total_subscriptions && emailStats?.verified_subscriptions !== undefined ? 
                  `${Math.round((emailStats.verified_subscriptions / emailStats.total_subscriptions) * 100)}%` : 
                  emailStats?.total_subscriptions === emailStats?.verified_subscriptions ? '100%' : '0%'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
        <button
          onClick={() => setShowCampaignModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Create Campaign</span>
        </button>
        
        <button
          onClick={() => {
            const unverifiedSubscribers = filteredSubscribers.filter(sub => !sub.isVerified && sub.isActive);
            if (unverifiedSubscribers.length === 0) {
              showError('No unverified subscribers to verify');
              return;
            }
            
            if (confirm(`Are you sure you want to verify all ${unverifiedSubscribers.length} unverified subscribers?`)) {
              Promise.all(unverifiedSubscribers.map(sub => verifySubscriber(sub.id)))
                .then(() => {
                  showSuccess(`Successfully verified ${unverifiedSubscribers.length} subscribers!`);
                })
                .catch(() => {
                  showError('Some verifications failed. Please try again.');
                });
            }
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <UserCheck className="h-4 w-4" />
          <span>Verify All Unverified</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search subscribers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Subscribers</option>
                <option value="active">Active Only</option>
                <option value="verified">Verified Only</option>
                <option value="unverified">Unverified Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Loading subscribers...</p>
            </div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No subscribers found</h3>
              <p className="text-gray-500">No subscribers match your current filters.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscriber
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscribed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {subscriber.firstName && subscriber.lastName 
                            ? `${subscriber.firstName} ${subscriber.lastName}`
                            : subscriber.email}
                        </div>
                        {subscriber.firstName && subscriber.lastName && (
                          <div className="text-sm text-gray-500">{subscriber.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        subscriber.isVerified 
                          ? 'bg-green-100 text-green-800'
                          : subscriber.isActive 
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {subscriber.isVerified ? 'Verified' : subscriber.isActive ? 'Pending' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscriber.source || 'Direct'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(subscriber.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscriber.verifiedAt ? new Date(subscriber.verifiedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        {!subscriber.isVerified && subscriber.isActive && (
                          <button
                            onClick={() => verifySubscriber(subscriber.id)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            title="Manually verify this subscriber"
                          >
                            <UserCheck className="h-3 w-3 mr-1" />
                            Verify
                          </button>
                        )}
                        {subscriber.isVerified && (
                          <span className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </span>
                        )}
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="More actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Campaign Creation Modal */}
      {showCampaignModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
          onClick={handleCampaignModalBackdropClick}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create Email Campaign</h2>
              <p className="text-sm text-gray-500 mt-1">Send a professional templated email to all subscribers</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* From Email Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send From
                </label>
                <select
                  value={campaignForm.fromEmail}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, fromEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {emailAliasOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {emailAliasOptions.find(o => o.value === campaignForm.fromEmail)?.description}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter email subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                <textarea
                  value={campaignForm.content}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your email content here..."
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your content will be wrapped in a professional StrymHub email template with header, footer, and unsubscribe link.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <select
                  value={campaignForm.targetAudience}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, targetAudience: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {audienceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {audienceOptions.find(o => o.value === campaignForm.targetAudience)?.description}
                </p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Recipients:</strong> This email will be sent to{' '}
                  {campaignForm.targetAudience === 'all' ? 'all registered users and newsletter subscribers' :
                   campaignForm.targetAudience === 'active' ? 'recently active registered users (logged in within 90 days)' :
                   campaignForm.targetAudience === 'admins' ? 'admin and staff users only' :
                   campaignForm.targetAudience === 'subscribers' ? `${emailStats?.verified_subscriptions || 0} newsletter subscribers only` :
                   'the selected audience'}.
                </p>
              </div>
              
              {/* Email Preview Note */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">📧 Email Template Preview</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>✓ Professional header with StrymHub branding</p>
                  <p>✓ Personalized greeting with subscriber's name</p>
                  <p>✓ Your subject as the main heading</p>
                  <p>✓ Your content formatted for email</p>
                  <p>✓ Call-to-action button to StrymHub</p>
                  <p>✓ Footer with unsubscribe link</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCampaignModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                onClick={sendCampaign}
                disabled={sending}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
                <span>{sending ? 'Sending...' : 'Send Now'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaigns;